import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { CheckCircle2, GraduationCap, Loader2, AlertTriangle, Coins } from 'lucide-react';
import { api, useAuth } from '../App';
import { Button } from '../components/ui/button';
import Header from '../components/Header';
import { Footer } from '../components/landing/Footer';

/**
 * CoOpVerify
 *
 * Landing page for the magic-link email. Decodes the token on mount,
 * posts to /api/co-op/verify-student/confirm, and shows a success or
 * failure state. On success it redirects to the account page after a
 * short pause so the user sees the "credits awarded" confirmation.
 */
export default function CoOpVerify() {
  const [params] = useSearchParams();
  const token = params.get('token');
  const { user } = useAuth();
  const navigate = useNavigate();

  // We don't want to re-fire confirm on hot-reload / strict-mode double-invoke.
  const inflight = useRef(false);
  const [status, setStatus] = useState('pending'); // pending | ok | error
  const [message, setMessage] = useState('');
  const [credits, setCredits] = useState(null);

  useEffect(() => {
    if (!token) {
      setStatus('error');
      setMessage('No verification token in the URL. Please use the link from your email.');
      return;
    }
    if (!user) {
      // Preserve the token across the login redirect so they land back here.
      navigate(`/login?next=${encodeURIComponent(`/co-op/verify?token=${token}`)}`);
      return;
    }
    if (inflight.current) return;
    inflight.current = true;

    (async () => {
      try {
        const res = await api.post('/co-op/verify-student/confirm', { token });
        setStatus('ok');
        setCredits(res.data?.credits ?? null);
        toast.success('Student status verified', {
          description: `Welcome to the Zuriya Co-Op. ${res.data?.credits ?? 0} credits added to your wallet.`,
        });
        // Give the user a beat to see the confirmation before bouncing.
        setTimeout(() => navigate('/settings', { replace: true }), 2800);
      } catch (err) {
        setStatus('error');
        setMessage(err?.response?.data?.detail || 'This verification link is invalid or has expired.');
      }
    })();
  }, [token, user, navigate]);

  return (
    <div className="min-h-screen bg-[#FAFAFA] flex flex-col">
      <Header />
      <main className="flex-1 flex items-center justify-center px-4 py-16">
        <div
          className="max-w-md w-full bg-white rounded-xl border border-stone-200 p-8 text-center"
          data-testid="co-op-verify-page"
        >
          {status === 'pending' && (
            <>
              <div className="h-14 w-14 rounded-full bg-stone-100 flex items-center justify-center mx-auto">
                <Loader2 className="h-6 w-6 text-stone-500 animate-spin" />
              </div>
              <h1 className="font-heading text-2xl text-[#1A1A1A] mt-5">Confirming your email...</h1>
              <p className="text-stone-500 text-sm mt-2">This takes a second.</p>
            </>
          )}

          {status === 'ok' && (
            <>
              <div className="h-14 w-14 rounded-full bg-[#D4AF37]/20 flex items-center justify-center mx-auto">
                <CheckCircle2 className="h-7 w-7 text-[#8a7320]" />
              </div>
              <h1 className="font-heading text-2xl text-[#1A1A1A] mt-5">You're in the Co-Op</h1>
              <p className="text-stone-600 text-sm mt-2 leading-relaxed">
                Student status verified. We've credited your wallet.
              </p>
              {credits !== null && (
                <div
                  className="mt-5 inline-flex items-center gap-2 border border-[#D4AF37]/50 bg-[#FAF8F2] rounded-lg px-4 py-2"
                  data-testid="co-op-verify-credits-badge"
                >
                  <Coins className="h-4 w-4 text-[#D4AF37]" />
                  <span className="font-mono text-sm">
                    <span className="font-semibold">{credits}</span> credits
                  </span>
                </div>
              )}
              <p className="text-stone-400 text-xs mt-6">
                Redirecting you to your account...
              </p>
            </>
          )}

          {status === 'error' && (
            <>
              <div className="h-14 w-14 rounded-full bg-red-50 flex items-center justify-center mx-auto">
                <AlertTriangle className="h-7 w-7 text-red-600" />
              </div>
              <h1 className="font-heading text-2xl text-[#1A1A1A] mt-5">We couldn't verify this link</h1>
              <p className="text-stone-600 text-sm mt-2 leading-relaxed">{message}</p>
              <div className="mt-6 flex flex-col gap-2">
                <Link to="/settings">
                  <Button className="w-full bg-[#1A1A1A] text-white hover:bg-[#2a2a2a]">
                    <GraduationCap className="h-4 w-4 mr-2" />
                    Request a fresh link
                  </Button>
                </Link>
                <Link to="/" className="text-xs text-stone-500 hover:text-stone-700">
                  Back to Zuriya
                </Link>
              </div>
            </>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
